import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  getOnboardingStatus,
  completeTutorial,
  skipTutorial,
  completeOnboarding,
} from '../../api/auth';

const OnboardingContext = createContext(null);

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}

export function OnboardingProvider({ children }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTutorial, setCurrentTutorial] = useState(null);

  // Fetch onboarding status
  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // API returns data directly, not wrapped in response.data
      const data = await getOnboardingStatus();
      setStatus(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch onboarding status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Complete a tutorial
  const handleCompleteTutorial = useCallback(async (tutorialKey) => {
    try {
      await completeTutorial(tutorialKey);
      setStatus(prev => ({
        ...prev,
        tutorials_completed: [...(prev?.tutorials_completed || []), tutorialKey],
      }));
      setCurrentTutorial(null);
    } catch (err) {
      console.error('Failed to complete tutorial:', err);
    }
  }, []);

  // Skip a tutorial
  const handleSkipTutorial = useCallback(async (tutorialKey) => {
    try {
      await skipTutorial(tutorialKey);
      setStatus(prev => ({
        ...prev,
        tutorials_completed: [...(prev?.tutorials_completed || []), tutorialKey],
      }));
      setCurrentTutorial(null);
    } catch (err) {
      console.error('Failed to skip tutorial:', err);
    }
  }, []);

  // Complete onboarding
  const handleCompleteOnboarding = useCallback(async () => {
    try {
      await completeOnboarding();
      setStatus(prev => ({
        ...prev,
        needs_onboarding: false,
      }));
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
    }
  }, []);

  // Check if a specific tutorial is completed
  const isTutorialCompleted = useCallback((tutorialKey) => {
    return status?.tutorials_completed?.includes(tutorialKey) || false;
  }, [status]);

  // Start a tutorial
  const startTutorial = useCallback((tutorialKey) => {
    setCurrentTutorial(tutorialKey);
  }, []);

  // Refresh status (for after state changes)
  const refresh = useCallback(() => {
    fetchStatus();
  }, [fetchStatus]);

  const value = {
    status,
    loading,
    error,
    currentTutorial,
    isTutorialCompleted,
    startTutorial,
    completeTutorial: handleCompleteTutorial,
    skipTutorial: handleSkipTutorial,
    completeOnboarding: handleCompleteOnboarding,
    refresh,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

OnboardingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default OnboardingContext;
